import React, { useEffect, useState } from "react"
import { useParams, useLocation, Link } from "react-router-dom"
import Skeleton from "react-loading-skeleton"
import {
  ArrowLeft, BadgeCheck, MapPin, Link2, Calendar, RefreshCw, Users, Film,
  Eye, ShieldOff, SearchX, AlertTriangle, ExternalLink, Hash,
} from "lucide-react"

import useInfluencerProfile, { PROFILE_STATUS } from "../hooks/useInfluencerProfile"
import { fetchInfluencerProfile } from "../influencerProfileApi"
import { useAnalytics, ANALYTICS_EVENTS } from "../../../analytics"
import Avatar from "./Avatar"
import FavouriteButton from "./FavouriteButton"
import AppCoverageCard from "./AppCoverageCard"
import { INFLUENCER_PLATFORMS, INFLUENCER_TIER_LABEL } from "../constants"
import { formatViews, formatUtcDate, buildFallbackProfileUrl } from "../utils"

// Preview posts per app card - a useful grid without pulling the full 50-cap
// for every app up front; AppCoverageCard's "see all" action re-requests one
// app at a time with the full cap (INFLUENCER_PROFILE_API.md §8).
const DEFAULT_VIDEOS_PER_APP = 12

const SidebarStat = ({ icon: Icon, label, value }) => {
  if (value === null || value === undefined) return null
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
      <span style={{ width: "30px", height: "30px", borderRadius: "8px", background: "rgba(255,255,255,0.05)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        <Icon size={14} color="#9ca3af" />
      </span>
      <div style={{ minWidth: 0 }}>
        <div style={{ color: "#fff", fontSize: "13px", fontWeight: 600, fontFamily: "Rubik" }}>{value}</div>
        <div style={{ color: "#6b7280", fontSize: "11px" }}>{label}</div>
      </div>
    </div>
  )
}

const EmptyPanel = ({ icon: Icon, title, children }) => (
  <div className="text-center" style={{ background: "rgba(30,31,41,0.4)", borderRadius: "16px", border: "1px solid rgba(255,255,255,0.05)", padding: "56px 20px" }}>
    <div style={{ width: "56px", height: "56px", background: "rgba(246,37,90,0.1)", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
      <Icon size={24} color="#F6255A" />
    </div>
    <h6 style={{ color: "#fff", fontWeight: 600, marginBottom: "8px", fontSize: "16px" }}>{title}</h6>
    <div style={{ color: "rgba(255,255,255,0.5)", fontSize: "13px", maxWidth: "420px", margin: "0 auto", lineHeight: "1.5" }}>{children}</div>
  </div>
)

// Returns to wherever the card was clicked from - including its filters,
// sort, page and mode, carried on that URL's own query string (urlState.js)
// - rather than always resetting to the bare `/influencer-explorer` list.
// Only a direct visit or a shared profile link (no `from` in router state)
// falls back to the plain list.
const BackLink = ({ to }) => (
  <Link
    to={to}
    style={{ display: "inline-flex", alignItems: "center", gap: "7px", color: "#9ca3af", fontSize: "13px", fontWeight: 600, fontFamily: "Rubik", textDecoration: "none", marginBottom: "20px" }}
    onMouseEnter={(e) => (e.currentTarget.style.color = "#fff")}
    onMouseLeave={(e) => (e.currentTarget.style.color = "#9ca3af")}
  >
    <ArrowLeft size={15} /> Back to Influencer Explorer
  </Link>
)

/**
 * The Influencer Explorer profile detail screen -
 * `/influencer-explorer/:platform/:username`. Renders one of three answers
 * INFLUENCER_PROFILE_API.md's endpoint can give (§3/§4/§6): a full creator
 * profile with their app coverage, a "this is an official account, not a
 * creator" answer, or a calm "not discovered yet" state for the 404 case -
 * which the docs are explicit is a real, valid answer, not a failure.
 */
const InfluencerProfileSection = ({ onTitleReady }) => {
  const { platform, username } = useParams()
  const location = useLocation()
  const { trackEvent } = useAnalytics()
  // The common path: navigated here from a leaderboard/favourites card,
  // which hands its row's real `profile_url` along as router state so no
  // extra resolution step is needed. A direct visit/hard refresh has no
  // state - useInfluencerProfile falls back to `platform`+`username` itself.
  const profileUrlFromState = location.state?.profileUrl
  // The exact list URL (filters/sort/page/mode and all) the card was
  // clicked from - InfluencerCard captures it, urlState.js is what put it
  // on that URL in the first place. Falls back to the bare list for a
  // direct visit or a shared profile link, which carries no such state.
  const backTo = location.state?.from || "/influencer-explorer"

  const [expandedApps, setExpandedApps] = useState({}) // app_id -> { videos, video_count }
  const [loadingAppId, setLoadingAppId] = useState(null)

  const { data, status, error, reload } = useInfluencerProfile({
    profileUrl: profileUrlFromState,
    platform,
    username,
    videosPerApp: DEFAULT_VIDEOS_PER_APP,
  })

  const influencer = data?.influencer
  const platformMeta = INFLUENCER_PLATFORMS.find(p => p.id === (influencer?.platform || platform))

  useEffect(() => {
    if (influencer && onTitleReady) {
      onTitleReady(influencer.full_name || influencer.username, platformMeta?.label)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [influencer?.username, influencer?.full_name])

  const handleLoadAllForApp = async (app) => {
    trackEvent(ANALYTICS_EVENTS.FEATURE.INFLUENCER_APP_COVERAGE_EXPANDED, {
      app_id: app.app_id,
      platform: influencer?.platform || platform,
      username,
    })
    setLoadingAppId(app.app_id)
    try {
      const result = await fetchInfluencerProfile({
        profileUrl: profileUrlFromState || `@${username}`,
        platform,
        appId: app.app_id,
        videosPerApp: 50,
      })
      const fullApp = (result.apps || []).find(a => a.app_id === app.app_id) || result.apps?.[0]
      if (fullApp) {
        setExpandedApps(prev => ({ ...prev, [app.app_id]: fullApp }))
      }
    } catch {
      // Leave the card as-is - the "see all" action failing quietly is
      // preferable to losing the preview posts already on screen.
    } finally {
      setLoadingAppId(null)
    }
  }

  if (status === PROFILE_STATUS.LOADING) {
    return (
      <div>
        <BackLink to={backTo} />
        <div style={{ display: "flex", gap: "16px", marginBottom: "24px" }}>
          <Skeleton circle width={72} height={72} baseColor="#2a2a35" highlightColor="#33333f" />
          <div style={{ flex: 1 }}>
            <Skeleton width="40%" height={20} baseColor="#2a2a35" highlightColor="#33333f" />
            <Skeleton width="25%" baseColor="#2a2a35" highlightColor="#33333f" />
          </div>
        </div>
        <Skeleton height={120} baseColor="#2a2a35" highlightColor="#33333f" style={{ marginBottom: "16px" }} />
        <Skeleton height={200} baseColor="#2a2a35" highlightColor="#33333f" />
      </div>
    )
  }

  if (status === PROFILE_STATUS.NOT_FOUND) {
    const fallbackUrl = buildFallbackProfileUrl(platform, username)
    return (
      <div>
        <BackLink to={backTo} />
        <EmptyPanel icon={SearchX} title="We haven't discovered this account yet">
          <p style={{ margin: "0 0 12px" }}>
            Our creator directory hasn't seen <strong style={{ color: "#e8e8e8" }}>@{username}</strong> on {platformMeta?.label || platform} yet -
            discovery runs on its own schedule, so this may simply not have come up yet, not that something went wrong.
          </p>
          {fallbackUrl && (
            <a
              href={fallbackUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "#F6255A", fontWeight: 600, textDecoration: "none" }}
              onMouseEnter={(e) => (e.currentTarget.style.textDecoration = "underline")}
              onMouseLeave={(e) => (e.currentTarget.style.textDecoration = "none")}
            >
              Check @{username} on {platformMeta?.label || platform} <ExternalLink size={12} style={{ verticalAlign: "-1px" }} />
            </a>
          )}
        </EmptyPanel>
      </div>
    )
  }

  if (status === PROFILE_STATUS.ERROR) {
    return (
      <div>
        <BackLink to={backTo} />
        <EmptyPanel icon={AlertTriangle} title="Something went wrong">
          <p style={{ margin: "0 0 16px" }}>{typeof error === "string" ? error : "We couldn't load this creator's profile."}</p>
          <button
            onClick={reload}
            style={{ background: "rgba(246,37,90,0.1)", border: "1px solid #F6255A", color: "#F6255A", padding: "10px 24px", borderRadius: "10px", fontWeight: 600, fontFamily: "Rubik", cursor: "pointer" }}
          >
            Try Again
          </button>
        </EmptyPanel>
      </div>
    )
  }

  if (!influencer) return null

  // §4 - official accounts (an app's own, a studio's, a brand's) answer
  // 200 too, not 404 - a distinct, simpler state: never ranked, never
  // favouritable, no app-coverage section.
  if (data.found_in === "official_account") {
    return (
      <div>
        <BackLink to={backTo} />
        <div style={{ background: "#1c1d27", border: "1px solid rgba(255,255,255,0.06)", borderRadius: "16px", padding: "32px", display: "flex", alignItems: "center", gap: "18px", flexWrap: "wrap" }}>
          <Avatar src={influencer.profile_image_url} alt={influencer.username} size={64} placeholder />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
              <h1 style={{ color: "#fff", fontSize: "20px", fontWeight: 700, margin: 0, fontFamily: "Rubik" }}>
                {influencer.full_name || `@${influencer.username}`}
              </h1>
              <span style={{ display: "flex", alignItems: "center", gap: "5px", background: "rgba(255,255,255,0.06)", color: "#9ca3af", fontSize: "11px", fontWeight: 700, padding: "3px 10px", borderRadius: "999px", textTransform: "uppercase", letterSpacing: "0.03em" }}>
                <ShieldOff size={11} /> {influencer.account_type || "Official account"}
              </span>
            </div>
            <p style={{ color: "#9ca3af", fontSize: "13.5px", margin: "8px 0 0", lineHeight: "1.6" }}>
              This is an official <strong style={{ color: "#e8e8e8" }}>{influencer.account_type || "brand"}</strong> account,
              not an individual creator - it isn't part of the ranked leaderboard and can't be saved as a favourite.
            </p>
          </div>
        </div>
      </div>
    )
  }

  // §3 - the full creator profile.
  const apps = data.apps || []
  const description = influencer.description
  const externalLinks = Array.isArray(influencer.external_links) ? influencer.external_links : []
  const tierLabel = INFLUENCER_TIER_LABEL[influencer.creator_size]

  return (
    <div>
      <BackLink to={backTo} />

      {/* ── Header ─────────────────────────────────────────────────── */}
      <div style={{ background: "linear-gradient(120deg, rgba(246,37,90,0.07), rgba(28,29,39,0.6) 55%, rgba(28,29,39,0.6))", border: "1px solid rgba(255,255,255,0.08)", borderRadius: "18px", padding: "26px", marginBottom: "20px" }}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: "18px", flexWrap: "wrap" }}>
          <Avatar src={influencer.profile_image_url} alt={influencer.username} size={72} placeholder />
          <div style={{ flex: 1, minWidth: "220px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
              <h1 style={{ color: "#fff", fontSize: "22px", fontWeight: 700, margin: 0, fontFamily: "Rubik" }}>
                {influencer.full_name || `@${influencer.username}`}
              </h1>
              {influencer.is_verified && <BadgeCheck size={18} color="#3b82f6" title="Verified account" />}
              {typeof influencer.rank === "number" && (
                <span style={{ fontSize: "11px", fontWeight: 700, color: "#F6255A", background: "rgba(246,37,90,0.14)", padding: "3px 10px", borderRadius: "999px" }}>
                  #{influencer.rank} overall
                </span>
              )}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap", marginTop: "6px" }}>
              <span style={{ color: "#9ca3af", fontSize: "13px" }}>@{influencer.username}</span>
              {platformMeta && (
                <span style={{ display: "flex", alignItems: "center", gap: "5px", background: `${platformMeta.color}18`, color: platformMeta.color, fontSize: "10.5px", fontWeight: 700, padding: "3px 8px", borderRadius: "6px" }}>
                  <i className={platformMeta.icon} style={{ fontSize: "10px" }} /> {platformMeta.label}
                </span>
              )}
              {tierLabel && (
                <span style={{ fontSize: "10.5px", fontWeight: 700, color: "#9ca3af", background: "rgba(255,255,255,0.05)", padding: "3px 8px", borderRadius: "6px" }}>
                  {tierLabel}
                </span>
              )}
            </div>

            {description && (
              <p style={{ color: "#c7c9d9", fontSize: "13.5px", lineHeight: "1.6", margin: "14px 0 0", maxWidth: "640px" }}>{description}</p>
            )}

            <div style={{ display: "flex", alignItems: "center", gap: "16px", flexWrap: "wrap", marginTop: "14px" }}>
              {influencer.location && (
                <span style={{ display: "flex", alignItems: "center", gap: "5px", color: "#9ca3af", fontSize: "12px" }}>
                  <MapPin size={12} /> {influencer.location}
                </span>
              )}
              {externalLinks.slice(0, 3).map((link, i) => (
                <a
                  key={i}
                  href={link}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ display: "flex", alignItems: "center", gap: "5px", color: "#9ca3af", fontSize: "12px", textDecoration: "none" }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = "#fff")}
                  onMouseLeave={(e) => (e.currentTarget.style.color = "#9ca3af")}
                >
                  <Link2 size={12} /> {link.replace(/^https?:\/\//, "").slice(0, 32)}
                </a>
              ))}
            </div>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: "10px", alignItems: "stretch" }}>
            <div style={{ textAlign: "right" }}>
              <div style={{ color: "#fff", fontSize: "22px", fontWeight: 700, fontFamily: "Rubik" }}>
                {influencer.audience_display || formatViews(influencer.audience_size)}
              </div>
              <div style={{ color: "#9ca3af", fontSize: "11.5px" }}>{influencer.audience_type || "audience"}</div>
            </div>
            <FavouriteButton
              platform={influencer.platform}
              username={influencer.username}
              isFavourite={influencer.is_favourite}
              size="labeled"
            />
            <a
              href={influencer.profile_url}
              target="_blank"
              rel="noopener noreferrer"
              style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "6px", color: "#9ca3af", fontSize: "12px", textDecoration: "none" }}
              onMouseEnter={(e) => (e.currentTarget.style.color = "#fff")}
              onMouseLeave={(e) => (e.currentTarget.style.color = "#9ca3af")}
              onClick={() => trackEvent(ANALYTICS_EVENTS.FEATURE.INFLUENCER_OUTBOUND_CLICKED, {
                platform: influencer.platform,
                username: influencer.username,
                link_type: "profile",
              })}
            >
              <ExternalLink size={11} /> Visit on {platformMeta?.label || influencer.platform}
            </a>
          </div>
        </div>
      </div>

      {/* ── Stats + apps layout ────────────────────────────────────── */}
      <div className="influencer-profile-grid">
        <div style={{ display: "flex", flexDirection: "column", gap: "18px" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <h5 style={{ color: "#fff", fontWeight: 600, fontSize: "16px", fontFamily: "Rubik", margin: 0 }}>
              Apps covered {data.total_apps ? `(${data.total_apps})` : ""}
            </h5>
            {data.total_videos != null && (
              <span style={{ color: "#6b7280", fontSize: "12px" }}>{data.total_videos} post{data.total_videos === 1 ? "" : "s"} total</span>
            )}
          </div>

          {apps.length === 0 ? (
            <EmptyPanel icon={Hash} title="No app coverage yet">
              We haven't stored any posts from this creator about an app.
            </EmptyPanel>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
              {apps.map((app) => (
                <AppCoverageCard
                  key={app.app_id}
                  app={expandedApps[app.app_id] || app}
                  requestedVideosPerApp={DEFAULT_VIDEOS_PER_APP}
                  onLoadAll={expandedApps[app.app_id] ? null : () => handleLoadAllForApp(app)}
                  isLoadingAll={loadingAppId === app.app_id}
                  platform={influencer.platform}
                  username={influencer.username}
                />
              ))}
            </div>
          )}
        </div>

        {/* Lines up with the left column's app cards rather than its
            "Apps covered" heading row - that heading has no counterpart in
            this column, so without this offset the card sat noticeably
            higher than the content beside it. */}
        <div style={{ display: "flex", flexDirection: "column", gap: "14px", marginTop: "40px" }}>
          <div style={{ background: "#1c1d27", border: "1px solid rgba(255,255,255,0.06)", borderRadius: "14px", padding: "18px", display: "flex", flexDirection: "column", gap: "14px" }}>
            <h6 style={{ color: "#fff", fontWeight: 600, fontSize: "12.5px", textTransform: "uppercase", letterSpacing: "0.04em", opacity: 0.7, margin: 0 }}>Creator stats</h6>
            <SidebarStat icon={Film} label="Posts" value={influencer.posts_count != null || influencer.videos_count != null ? formatViews(influencer.posts_count ?? influencer.videos_count) : null} />
            <SidebarStat icon={Eye} label="Total views" value={influencer.total_views != null ? formatViews(influencer.total_views) : null} />
            <SidebarStat icon={Users} label="Posts about apps" value={influencer.posts_about_apps || null} />
            <SidebarStat icon={Calendar} label="First discovered" value={formatUtcDate(influencer.first_seen_at)} />
            <SidebarStat icon={RefreshCw} label="Last refreshed" value={formatUtcDate(influencer.last_refreshed_at)} />
          </div>
        </div>
      </div>

      <style>{`
        .influencer-profile-grid {
          display: grid;
          grid-template-columns: minmax(0, 2fr) minmax(220px, 1fr);
          gap: 22px;
          align-items: start;
        }
        @media (max-width: 900px) {
          .influencer-profile-grid { grid-template-columns: 1fr; }
        }
      `}</style>
    </div>
  )
}

export default InfluencerProfileSection
